import { useState } from 'react';
import { COPY, programLabel } from '../content';
import { apiGet, apiPatch, apiPost, type MemberDetail as MemberData } from '../api';
import { useAsync, navigate } from '../hooks';
import { shortDate } from '../format';
import { Spinner, LoadError, Card, KV, Money, Pill, statusPill, lifecyclePill } from '../components/ui';

const TIER_LABEL: Record<string, string> = { paddler: 'Paddler (free)', owner: 'Owner', household: 'Household' };

function grantLabel(a: NonNullable<MemberData['ownerAccess']>): string {
  if (a.source === 'order') return `Order ${a.orderId ?? ''}${a.boardTitle ? ` (${a.boardTitle})` : ''}`;
  if (a.source === 'proof') return 'Confirmed proof';
  return `Manual${a.grantedReason ? ` (${a.grantedReason})` : ''}`;
}

const PROGRAMS = ['brand_ambassador', 'demo_center', 'pro_content_creator', 'inflatable_boarder'];

export function MemberDetail({ id, onChange }: { id: string; onChange: () => void }) {
  const { data, loading, error, reload } = useAsync(() => apiGet<MemberData>(`/v1/admin/members/${id}`), [id]);
  const [busy, setBusy] = useState(false);

  async function changeProgram(program: string) {
    if (!data || program === data.program) return;
    setBusy(true);
    try {
      await apiPatch(`/v1/admin/members/${id}/program`, { program });
      reload();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function changeLifecycle(status: string) {
    setBusy(true);
    try {
      await apiPatch(`/v1/admin/members/${id}/status`, { status });
      reload();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function grantAccess() {
    const reason = window.prompt(COPY.access.grantPrompt);
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await apiPost(`/v1/admin/members/${id}/grant-access`, { reason: reason.trim() });
      reload();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function revokeAccess() {
    const reason = window.prompt(COPY.access.revokePrompt);
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await apiPost(`/v1/admin/members/${id}/revoke-access`, { reason: reason.trim() });
      reload();
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button className="ghost" onClick={() => navigate('members')}>
        {'<- '}
        {COPY.common.back}
      </button>
      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <div className="row between" style={{ marginTop: 8 }}>
            <h1 className="screen-title">{data.name}</h1>
            <div className="row" style={{ gap: 6 }}>
              {lifecyclePill(data.lifecycleStatus)}
              {data.accountStatus === 'suspended' ? <Pill tone="red">Banned</Pill> : null}
            </div>
          </div>

          <Card>
            {data.email ? <KV k="Email (admin only)" v={<span className="small">{data.email}</span>} /> : null}
            <KV k="Crew number" v={data.crewNumber ? `#${data.crewNumber}` : 'Not claimed'} />
            <KV k="Program" v={programLabel(data.program)} />
            <KV k="Sharing tools" v={data.sharingToolsOn ? 'On' : 'Off'} />
            <KV k="Code" v={data.code ? <span className="mono">{data.code}</span> : 'None'} />
            {data.link ? <KV k="Link" v={<span className="mono small">{data.link}</span>} /> : null}
            <KV k="Joined" v={shortDate(data.joinDateIso)} />
            <KV k="Payout method" v={data.payoutMethod === 'cash' ? 'Cash' : 'Store credit'} />
            <KV k="Store credit" v={<Money cents={data.storeCreditBalanceCents} />} />
            <KV k="W-9" v={w9Label(data.w9Status)} />
            <KV k="Referrals this season" v={data.seasonReferrals} />
            <KV k="Days on the water" v={data.seasonPaddleDays} />
          </Card>

          <Card title={COPY.access.title}>
            <KV
              k="Tier"
              v={
                <Pill tone={data.tier === 'owner' ? 'blue' : data.tier === 'household' ? 'green' : 'gray'}>
                  {TIER_LABEL[data.tier] ?? data.tier}
                </Pill>
              }
            />
            {data.ownerAccess ? (
              <KV k="Access granted by" v={<span className="small">{grantLabel(data.ownerAccess)} | {shortDate(data.ownerAccess.grantedAtIso)}</span>} />
            ) : null}
            {data.householdOwner ? (
              <KV
                k="Household of"
                v={
                  <button className="ghost" onClick={() => navigate(`members/${data.householdOwner!.memberId}`)}>
                    {data.householdOwner.name}
                  </button>
                }
              />
            ) : null}
            {data.householdMembers.length > 0 ? (
              <KV
                k={`Household members (${data.householdMembers.length})`}
                v={
                  <div>
                    {data.householdMembers.map((h) => (
                      <button key={h.memberId} className="ghost" style={{ display: 'block' }} onClick={() => navigate(`members/${h.memberId}`)}>
                        {h.name} <span className="muted small">joined {shortDate(h.joinedAtIso)}</span>
                      </button>
                    ))}
                  </div>
                }
              />
            ) : null}
            <div className="toolbar" style={{ marginBottom: 0, marginTop: 8 }}>
              {data.tier === 'owner' ? (
                <button className="danger" disabled={busy} onClick={revokeAccess}>
                  {COPY.access.revoke}
                </button>
              ) : (
                <button className="secondary" disabled={busy} onClick={grantAccess}>
                  {COPY.access.grant}
                </button>
              )}
            </div>
          </Card>

          <Card title="Manage">
            <div className="toolbar" style={{ marginBottom: 0 }}>
              <select
                value={data.program}
                disabled={busy}
                onChange={(e) => changeProgram(e.target.value)}
                aria-label="Move program"
              >
                {PROGRAMS.map((p) => (
                  <option key={p} value={p}>
                    {programLabel(p)}
                  </option>
                ))}
              </select>
              {data.lifecycleStatus !== 'paused' ? (
                <button className="secondary" disabled={busy} onClick={() => changeLifecycle('paused')}>
                  Pause
                </button>
              ) : (
                <button className="secondary" disabled={busy} onClick={() => changeLifecycle('active')}>
                  Resume
                </button>
              )}
              {data.lifecycleStatus !== 'closed' ? (
                <button className="danger" disabled={busy} onClick={() => changeLifecycle('closed')}>
                  Close
                </button>
              ) : null}
            </div>
          </Card>

          <Card title={`Sales (${data.sales.length})`}>
            {data.sales.length === 0 ? <p className="muted small">No sales yet.</p> : null}
            {data.sales.map((s) => (
              <div className="list-row" key={s.id}>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{s.orderName ?? 'Order'}</strong>
                    {statusPill(s.status)}
                  </div>
                  <div className="small muted">
                    {s.productType ?? 'a sale'} | {shortDate(s.processedAtIso)} | {s.attributionType === 'code' ? 'by code' : 'by link'}
                  </div>
                </div>
                <Money cents={s.amountCents} />
              </div>
            ))}
          </Card>

          <Card title={`Payouts (${data.payouts.length})`}>
            {data.payouts.length === 0 ? <p className="muted small">No payouts yet.</p> : null}
            {data.payouts.map((p) => (
              <div className="list-row" key={p.id}>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{p.method === 'store_credit' ? 'Store credit' : p.method}</strong>
                    <Pill tone={p.status === 'sent' ? 'green' : p.status === 'failed' ? 'red' : 'amber'}>
                      {p.status === 'sent' ? 'Sent' : p.status === 'failed' ? 'Failed' : 'Queued'}
                    </Pill>
                  </div>
                  <div className="small muted">
                    {shortDate(p.sentAtIso ?? p.createdAtIso)}
                    {p.failureReason ? ` | ${p.failureReason}` : ''}
                  </div>
                </div>
                <Money cents={p.totalCents} />
              </div>
            ))}
          </Card>

          <Card title="History">
            {data.history.length === 0 ? <p className="muted small">No recorded actions yet.</p> : null}
            {data.history.map((h) => (
              <div className="kv" key={h.id}>
                <span className="k">
                  {h.action.replace(/_/g, ' ')} <span className="muted">by {h.actor}</span>
                </span>
                <span className="v small muted">{shortDate(h.atIso)}</span>
              </div>
            ))}
          </Card>
        </>
      ) : null}
    </div>
  );
}

function w9Label(status: string): string {
  if (status === 'on_file') return 'On file';
  if (status === 'requested') return 'Requested';
  return 'Not needed yet';
}
